<script lang="ts">
    // https://github.com/chanced/filedrop-svelte
    import FileDrop from 'filedrop-svelte';
    import { get } from 'svelte/store';
    import type { Files } from 'filedrop-svelte';
    import { Navigation } from '../../utilities/Navigation';
    import { _ } from 'svelte-i18n';

    import { ImportApi } from './ImportApi';
    import { api } from '../../../stores/api';
    import {
        currentImport,
        ImportResponseSchema,
    } from '../../shared/ColumnSelectPro/currentImport';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import { openModal } from 'svelte-modals';
    import ModalError from '../../shared/Modals/ModalError.svelte';
    import { captureMessage } from '@sentry/svelte';
    import { app } from '../../../stores/app';
    import WarningInline from '../../shared/WarningInline.svelte';
    import InlineInfoAboutExpiredAccount from '../../shared/InlineInfoAboutExpiredAccount.svelte';
    import { ExportTypes } from '../Export/ExportTypes';
    import { onMount } from 'svelte';
    import IconUpload from '../../shared/Icons/IconUpload.svelte';
    import { Umami } from '../../utilities/Umami';

    // import fileSize from "filesize";
    let files: Files;
    let error: string = '';
    let uploadingInProgress: boolean = false;
    let value: string = 'file.txt';
    // const accept: string = '.xlsx, .xls, .csv, .ods'; // https://stackoverflow.com/questions/11832930/html-input-file-accept-attribute-file-type-csv#11834872
    // const accept: string = '.xlsx, .csv, .ods'; // https://stackoverflow.com/questions/11832930/html-input-file-accept-attribute-file-type-csv#11834872
    let acceptImportFileTypes: string | null = null; // = ExportTypes.getEnabledTypesString();

    function upload() {
        error = '';
        uploadingInProgress = true;
        let importName = 'import_' + new Date().toISOString().slice(0, 10);
        ImportApi.create(get(api), {
            name: importName,
            import_type: 'upsert',
            file_type: 'locations',
        })
            .then(response => {
                console.log('createImport response', response);
                ImportResponseSchema.parse(response);
                $currentImport = response;

                if ($currentImport !== null) {
                    get(api)
                        .sendFiles<unknown>(
                            files.accepted,
                            value,
                            `/import/${$currentImport.id}/upload`
                        )
                        .then(response => {
                            console.log('upload response', response);
                            Umami.event('import_file_upload', { import_type: 'upsert', file_type: 'locations' });
                            Navigation.navigate('/assign-columns');
                        })
                        .catch(response => {
                            console.error('error response', response);
                            if (response.code === 'import-upload-too-many-lines') {
                                let currentLines = response.variables[0].current ?? null;
                                let maxLines = response.variables[0].max ?? null;
                                if (currentLines !== null && maxLines !== null) {
                                    openModal(ModalError, {
                                        header: $_('error'),
                                        message: $_(
                                            'too_many_lines_current_max',
                                            { values: { currentLines, maxLines } }
                                        ),
                                    });
                                    Umami.event('import_file_upload_too_many_lines', { import_type: 'upsert', file_type: 'locations', currentLines, maxLines });
                                } else if (maxLines !== null) {
                                    openModal(ModalError, {
                                        header: $_('error'),
                                        message: $_(
                                            'too_many_lines_max',
                                            { values: { maxLines } }
                                        ),
                                    });
                                    Umami.event('import_file_upload_too_many_lines', { import_type: 'upsert', file_type: 'locations', maxLines });
                                } else {
                                    openModal(ModalError, {
                                        header: $_('error'),
                                        message: $_('too_many_lines'),
                                    });
                                    Umami.event('import_file_upload_too_many_lines', { import_type: 'upsert', file_type: 'locations' });
                                }
                            } else {
                                openModal(ModalError, {
                                    header: $_('error'),
                                    message: $_('upload_file_error'),
                                });
                                Umami.event('import_file_upload_error', { import_type: 'upsert', file_type: 'locations', error: response.code ?? 'unknown_error' });
                            }
                            if ($currentImport !== null) {
                                $currentImport.columns_assignment = null;
                            }
                        })
                        .finally(() => {
                            uploadingInProgress = false;
                        });
                }
            })
            .catch(err => {
                error = $_('create_import_error', { values: { err } });
                uploadingInProgress = false;
            });
    }

    onMount( () => {
        ImportApi.getAvailableTypes($api).then( (types: string[]) => {
            acceptImportFileTypes = types.map(t => '.' + t).join(', ');
        });
    })

    $: {
        if (files && files.accepted) {
            upload();
        }
    }
</script>

{#if uploadingInProgress || acceptImportFileTypes === null}
    <Spinner />
{:else}
    {#if error}
        <MessageDanger {error} />
    {/if}
    {#if $app.account && $app.account?.isExpired() === true}
        <InlineInfoAboutExpiredAccount />
    {:else}
        <div class="filedrop-container">
            <FileDrop
                accept={acceptImportFileTypes}
                on:filedrop={e => {
                    files = e.detail.files;
                }}
            >
                <div class="upload-container">
                    <div class="upload-inner">
                        <div class="upload-icon">
                            <IconUpload scale={2}/>
                        </div>
                        <div class="upload-text">{$_('Drag and drop files here')}</div>
                        <div class="upload-link">{$_('Browse your device')}</div>
                    </div>
                </div>
            </FileDrop>
            <div class="upload-info">
                <div class="upload-text">{$_('Available formats')}: {acceptImportFileTypes}</div>
                <a
                    href="./examples/AutoMapa_LastMile__example_input_file.xlsx"
                    download
                    class="upload-link">{$_('Get sample file')}</a
                >
            </div>
        </div>
    {/if}
{/if}

<!--{#if files}-->
<!--    <h3>Accepted files</h3>-->
<!--    <ul>-->
<!--        {#each files.accepted as file}-->
<!--            <li>{file.name} </li>-->
<!--        {/each}-->
<!--    </ul>-->
<!--    <h3>Rejected files</h3>-->
<!--    <ul>-->
<!--        {#each files.rejected as rejected}-->
<!--            <li>{rejected.file.name} - {rejected.error.message}</li>-->
<!--        {/each}-->
<!--    </ul>-->
<!--{/if}-->

<style>
    .filedrop-container {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        font-size: 14px;
    }
    :global(.filedrop-container label) {
        width: 70%;
    }
    .upload-container {
        margin-top: 50px;
        /*width: 100%;*/
        /*max-width: 500px;*/
        height: 300px;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        font-size: 14px;
        /*min-width: 430px;*/
        width: 100%;
    }
    .upload-inner {
        border: 2px dashed #1d5fc9;
        border-radius: 15px;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        row-gap: 10px;
        cursor: pointer;
    }
    .upload-inner:hover {
        border: 2px dashed #1b5abf;
        background-color: #469ef444;
        transition: 0.3s;
    }
    .upload-icon {
        width: 76px;
        height: 71px;
        opacity: 0.6;
        color: #1d5fc9;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .upload-link {
        color: #1d5fc9;
        text-decoration: none;
        cursor: pointer;
        text-align: center;
    }
    .upload-link:hover {
        text-decoration: underline;
    }
    .upload-info {
        padding: 20px;
        display: flex;
        justify-content: center;
        flex-direction: column;
        row-gap: 10px;
    }
    label.button-primary {
        display: inline-block;
        border: 1px solid #1d5fc9;
        border-radius: 5px;
        background-color: #1d5fc9;
        color: #ffffff;
        height: 27px;
        line-height: 24px;
        font-size: 14px;
        box-sizing: border-box;
        cursor: pointer;
        margin: 2px 6px;
        margin-right: 6px;
        margin-bottom: 2px;
        text-align: center;
    }
</style>
